import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  // Handle format like "9:00 AM" (already 12h)
  if (time24.includes('AM') || time24.includes('PM')) {
    return time24;
  }
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Get end time by adding 30 minutes to start time
function getEndTime(startTime) {
  if (!startTime) return '';
  // Handle "9:00 AM" format
  if (startTime.includes('AM') || startTime.includes('PM')) {
    const [timePart, period] = startTime.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
    let newMinutes = minutes + 30;
    let newHours = hours;
    let newPeriod = period;
    
    if (newMinutes >= 60) {
      newMinutes -= 60;
      newHours += 1;
      if (newHours === 12 && period === 'AM') newPeriod = 'PM';
      else if (newHours === 12 && period === 'PM') newPeriod = 'AM';
      else if (newHours > 12) newHours -= 12;
    }
    return `${newHours}:${newMinutes.toString().padStart(2, '0')} ${newPeriod}`;
  }
  // Handle "09:00" format
  const [hours, minutes] = startTime.split(':').map(Number);
  let newMinutes = minutes + 30;
  let newHours = hours;
  if (newMinutes >= 60) {
    newMinutes -= 60;
    newHours += 1;
  }
  const period = newHours >= 12 ? 'PM' : 'AM';
  const hours12 = newHours % 12 || 12;
  return `${hours12}:${newMinutes.toString().padStart(2, '0')} ${period}`;
}

function MyBookings({ token, refreshKey }) {
  const [bookings, setBookings] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [timezone, setTimezone] = useState('America/New_York');

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/my-bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      setBookings([]);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings, refreshKey]);

  async function handleCancel(booking) {
    setMessage('');
    setError('');
    const slotText = booking.slot_count > 1 ? `${booking.slot_count} time slots` : 'this booking';
    if (!window.confirm(`Are you sure you want to cancel ${slotText}?`)) return;
    try {
      // If booking has a group ID, cancel the whole group
      const url = booking.booking_group_id 
        ? `${API_URL}/api/bookings/${booking.id}?booking_group_id=${booking.booking_group_id}`
        : `${API_URL}/api/bookings/${booking.id}`;
      
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Cancel failed');
      setMessage(data.message || 'Booking cancelled successfully.');
      fetchBookings();
    } catch (err) {
      setError(err.message);
    }
  }

  const formatDate = (dateStr) => {
    // Parse as local date to avoid timezone shift
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return '$' + Number(amount).toFixed(2);
  };

  const formatBookingType = (type) => {
    const typeMap = {
      'full_turf': 'Full Turf',
      'half_turf': 'Half Turf',
      'cage_rental': 'Cage'
    };
    return typeMap[type] || type.replace('_', ' ');
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: '#2563eb',
      approved: '#22c55e',
      rejected: '#ef4444'
    };
    return (
      <span style={{
        background: colors[status] || '#9ca3af',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  // Get formatted time range display
  const getTimeDisplay = (booking) => {
    const slots = booking.time_slots || [booking.time_slot];
    if (!slots || slots.length === 0) return { start: '-', end: '-' };
    
    // Sort slots
    const sortedSlots = [...slots].sort();
    const firstSlot = sortedSlots[0];
    const lastSlot = sortedSlots[sortedSlots.length - 1];
    
    // Get start time from first slot
    const startTime = formatTime12h(firstSlot.includes(' - ') ? firstSlot.split(' - ')[0] : firstSlot);
    
    // Get end time from last slot (add 30 min to the start of last slot)
    const lastSlotStart = lastSlot.includes(' - ') ? lastSlot.split(' - ')[0] : lastSlot;
    const endTime = getEndTime(lastSlotStart);
    
    return { start: startTime, end: endTime };
  };

  const filteredBookings = activeTab === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === activeTab);

  const getCounts = () => {
    return {
      all: bookings.length,
      pending: bookings.filter(b => b.status === 'pending').length,
      approved: bookings.filter(b => b.status === 'approved').length,
      rejected: bookings.filter(b => b.status === 'rejected').length
    };
  };

  const counts = getCounts();

  return (
    <div className="my-bookings-container">
      <h2>My Bookings</h2>
      
      <div className="bookings-tabs">
        <button 
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All <span className="tab-count">{counts.all}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending <span className="tab-count pending">{counts.pending}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          Approved <span className="tab-count approved">{counts.approved}</span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => setActiveTab('rejected')}
        >
          Rejected <span className="tab-count rejected">{counts.rejected}</span>
        </button>
      </div>

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}
      {filteredBookings.length === 0 ? (
        <p className="no-bookings">
          {activeTab === 'all' ? 'You have no bookings.' : `No ${activeTab} bookings.`}
        </p>
      ) : (
        <table className="bookings-table">
          <thead>
            <tr>
              <th>Time Slot</th>
              <th>Type</th>
              <th>Total Price</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map(b => {
              const timeDisplay = getTimeDisplay(b);
              const slotCount = b.slot_count || (b.time_slots ? b.time_slots.length : 1);
              return (
                <tr key={b.booking_group_id || b.id}>
                  <td className="time-slot-cell">
                    <div className="time-slot-date">
                      {formatDate(b.booking_date)}
                      {slotCount > 1 && (
                        <span className="slot-count-badge">({slotCount} slots)</span>
                      )}
                      {b.created_by && (
                        <span className="admin-booked-indicator" title={`Booked by ${b.created_by_first_name && b.created_by_last_name ? `${b.created_by_first_name} ${b.created_by_last_name}` : b.created_by_username}`}>
                          (Admin)
                        </span>
                      )}
                    </div>
                    <div className="time-slot-range">
                      {timeDisplay.start} - {timeDisplay.end}
                    </div>
                  </td>
                  <td>{formatBookingType(b.booking_type)}</td>
                  <td>{formatMoney(b.price)}</td>
                  <td>{getStatusBadge(b.status)}</td>
                  <td>
                    {b.status === 'pending' && (
                      <button className="cancel-btn" onClick={() => handleCancel(b)}>Cancel</button>
                    )}
                    {b.status === 'approved' && (
                      <span className="contact-admin-note">Contact admin to change</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MyBookings;
